import Swal from 'sweetalert2';

/** Toast en el documento cuando la pestaña está visible (Chrome/Windows a menudo no muestra globo del SO con el foco en la pestaña). */
const realtimeToast = Swal.mixin({
  toast: true,
  position: 'top',
  showConfirmButton: false,
  timer: 7000,
  timerProgressBar: true,
});

/**
 * Aviso dentro del navegador (no depende de la API Notification).
 * @param {string} title
 * @param {string} [body]
 * @param {boolean} [enabled=true]
 * @param {boolean} [silent=false]
 */
export function showInPageToastWhenTabVisible(title, body, enabled = true, silent = false) {
  if (!enabled || silent) return;
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
  const t = title == null || title === '' ? 'Aviso' : String(title);
  const b = body == null ? '' : String(body);
  try {
    realtimeToast.fire({
      icon: 'info',
      title: t,
      ...(b ? { text: b } : {}),
    });
  } catch (_) {
    /* sin fallback */
  }
}

function supportsBrowserNotifications() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/** HTTP en localhost/127.0.0.1 es contexto válido para notificaciones; no bloquear si `isSecureContext` falla en algún edge case. */
function isNotificationContextAllowed() {
  if (typeof window === 'undefined') return false;
  if (window.isSecureContext === true) return true;
  try {
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
  } catch (_) {
    return false;
  }
}

/** Evita que el recolector quite la notificación antes de que el SO la pinte (p. ej. redirección inmediata). */
const notificationKeepalive = [];
function retainNotificationInstance(n, ms = 5000) {
  notificationKeepalive.push(n);
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    const i = notificationKeepalive.indexOf(n);
    if (i >= 0) notificationKeepalive.splice(i, 1);
  }, ms);
}

/**
 * Icono opcional. Si el fichero no existe (404), algunos entornos Windows/Chrome
 * fallan al mostrar el globo si el `icon` va en el primer intento.
 */
export function getBrowserNotificationIconUrl() {
  try {
    const base = window.location?.origin || '';
    if (!base) return undefined;
    return `${base}/favicon.ico`;
  } catch (_) {
    return undefined;
  }
}

export function requestBrowserNotificationPermissionFromGesture() {
  if (!supportsBrowserNotifications()) return Promise.resolve('unsupported');
  if (Notification.permission === 'granted') return Promise.resolve('granted');
  if (Notification.permission === 'denied') return Promise.resolve('denied');
  return Notification.requestPermission().catch(() => 'denied');
}

function attachNotificationClick(n) {
  n.onclick = () => {
    try {
      const raw = n.data && n.data.url;
      if (raw && typeof raw === 'string') {
        window.focus();
        window.location.assign(
          raw.startsWith('http')
            ? raw
            : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`,
        );
      } else {
        window.focus();
      }
    } catch (_) {
      try {
        window.focus();
      } catch (_) {}
    }
    n.close();
  };
}

/**
 * Notificación del sistema operativo (centro de notificaciones / bandeja), no dentro del DOM.
 * No es "push" en sentido técnico web (eso implica servidor + Push API con suscripción).
 *
 * @param {string} title
 * @param {string} [body]
 * @param {NotificationOptions & { url?: string }} [extraOptions]
 * @returns {Promise<boolean>}
 */
export function notifyBrowser(title, body, extraOptions = {}) {
  if (!supportsBrowserNotifications()) {
    return Promise.resolve(false);
  }

  const titleStr = title == null || title === '' ? 'Notificación' : String(title);
  const bodyStr = body == null ? '' : String(body);
  const { url: openPath, data: dataOpt } = extraOptions;
  const dataPayload =
    dataOpt != null
      ? dataOpt
      : openPath != null && openPath !== ''
        ? { url: String(openPath) }
        : undefined;

  const show = () => {
    if (!isNotificationContextAllowed()) {
      return false;
    }

    const withMeta = {
      body: bodyStr,
      tag: extraOptions.tag,
      requireInteraction: !!extraOptions.requireInteraction,
      silent: !!extraOptions.silent,
      data: dataPayload,
    };

    /** Del más simple al más completo: fallos silenciosos si `renotify`/icon rompen en un navegador. */
    const attempts = [
      { body: bodyStr },
      { ...withMeta },
      ...(extraOptions.renotify ? [{ ...withMeta, renotify: true }] : []),
    ];

    const icon = extraOptions.icon ?? getBrowserNotificationIconUrl();
    if (icon) {
      attempts.push({
        ...withMeta,
        renotify: !!extraOptions.renotify,
        icon,
      });
    }

    for (const cfg of attempts) {
      try {
        const n = new Notification(titleStr, cfg);
        attachNotificationClick(n);
        retainNotificationInstance(n);
        return true;
      } catch (_) {
        /* siguiente variante */
      }
    }
    return false;
  };

  if (Notification.permission === 'granted') {
    return Promise.resolve(show());
  }
  if (Notification.permission === 'denied') {
    return Promise.resolve(false);
  }

  return Notification.requestPermission()
    .then((permission) => {
      if (permission !== 'granted') return false;
      return show();
    })
    .catch(() => false);
}
